import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { createReadinessSessionId, deleteReadinessConversation, getReadinessHistory, streamReadinessMessage } from "@/lib/readinessChat";
import { ChatMessage } from "./ChatMessage";

const SESSION_KEY = "tenet-readiness-chat-session";
const modes = [
  { id: "readiness", label: "Readiness guide", greeting: "Let’s begin gently. What kind of change are you hoping to make, and why does it matter to you now?" },
  { id: "results", label: "Explain results", greeting: "Share the name of your profile or a readiness dimension you want to understand. You can keep the details general." },
  { id: "tenet", label: "TENET questions", greeting: "What would you like to know about TENET’s approach, Five Pillars, or readiness assessment?" },
];

const getStoredSession = () => {
  const existing = window.sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const created = createReadinessSessionId();
  window.sessionStorage.setItem(SESSION_KEY, created);
  return created;
};

export const ReadinessChat = () => {
  const [sessionId, setSessionId] = useState(getStoredSession);
  const [mode, setMode] = useState("readiness");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const requestRef = useRef(null);
  const activeMode = useMemo(() => modes.find((item) => item.id === mode), [mode]);
  const visibleMessages = messages.length ? messages : [{ role: "assistant", content: activeMode.greeting }];

  useEffect(() => {
    const controller = new AbortController();
    getReadinessHistory(sessionId, controller.signal)
      .then((data) => setMessages(data.messages || []))
      .catch((historyError) => {
        if (historyError.name !== "AbortError") setError("A previous conversation could not be restored. You can still begin here.");
      })
      .finally(() => setLoadingHistory(false));
    return () => controller.abort();
  }, [sessionId]);

  useEffect(() => {
    const container = scrollRef.current;
    if (container) container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  useEffect(() => () => requestRef.current?.abort(), []);

  const sendMessage = async (event) => {
    event?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;

    const assistantId = `assistant-${Date.now()}`;
    setInput("");
    setError("");
    setStreaming(true);
    setMessages((current) => [...current, { role: "user", content: text }, { id: assistantId, role: "assistant", content: "" }]);
    const controller = new AbortController();
    requestRef.current = controller;

    try {
      await streamReadinessMessage({
        sessionId,
        message: text,
        mode,
        signal: controller.signal,
        onDelta: (content) => setMessages((current) => current.map((item) => item.id === assistantId ? { ...item, content: item.content + content } : item)),
      });
    } catch (sendError) {
      if (sendError.name !== "AbortError") {
        setError(sendError.message || "The TENET guide is unavailable right now.");
        setMessages((current) => current.filter((item) => item.id !== assistantId || item.content));
      }
    } finally {
      requestRef.current = null;
      setStreaming(false);
    }
  };

  const startNewConversation = async () => {
    requestRef.current?.abort();
    try {
      await deleteReadinessConversation(sessionId);
      const nextSession = createReadinessSessionId();
      window.sessionStorage.setItem(SESSION_KEY, nextSession);
      setMessages([]);
      setInput("");
      setError("");
      setSessionId(nextSession);
      toast.success("A new private conversation is ready.");
    } catch (resetError) {
      toast.error(resetError.message || "The conversation could not be reset.");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage(event);
    }
  };

  return (
    <div className="overflow-hidden border border-[#d9d6cc] bg-[#fbfaf6] shadow-[0_22px_70px_rgba(5,28,30,0.2)]" data-testid="readiness-chat-panel">
      <div className="flex items-center justify-between border-b border-[#dad8d0] px-5 py-5 md:px-7" data-testid="readiness-chat-header">
        <div>
          <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#567268]" data-testid="readiness-chat-status"><span className="h-2 w-2 rounded-full bg-[#6c957a]" /> AI guide</p>
          <h3 className="mt-2 font-serif text-3xl text-[#1b3f42]" data-testid="readiness-chat-title">What would help today?</h3>
        </div>
        <ShieldCheck className="text-[#a06f3e]" size={20} aria-hidden="true" />
      </div>

      <div className="grid grid-cols-3 border-b border-[#dad8d0] bg-[#f2f0e9]" data-testid="readiness-chat-modes">
        {modes.map((item) => (
          <button key={item.id} type="button" onClick={() => setMode(item.id)} disabled={streaming} className={`readiness-mode-button ${mode === item.id ? "is-active" : ""}`} data-testid={`readiness-chat-mode-${item.id}`} aria-pressed={mode === item.id}>
            {item.label}
          </button>
        ))}
      </div>

      <div ref={scrollRef} className="h-[360px] overflow-y-auto px-5 py-6 md:h-[390px] md:px-7" aria-live="polite" data-testid="readiness-chat-messages">
        {loadingHistory ? <p className="text-sm text-[#69756e]" data-testid="readiness-chat-history-loading">Preparing your guide…</p> : visibleMessages.map((message, index) => <ChatMessage key={message.id || `${message.role}-${index}`} message={message} index={index} streaming={streaming && index === visibleMessages.length - 1 && message.role === "assistant"} />)}
      </div>

      <form onSubmit={sendMessage} className="border-t border-[#dad8d0] p-4 md:p-5" data-testid="readiness-chat-form">
        <div className="flex items-end gap-3 border border-[#cbc9c0] bg-white p-2 focus-within:border-[#a06f3e]">
          <Textarea value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={handleKeyDown} maxLength={1200} disabled={streaming} placeholder="Share what’s on your mind…" aria-label="Message the TENET guide" data-testid="readiness-chat-input" className="min-h-12 resize-none border-0 px-2 py-3 shadow-none focus-visible:ring-0" />
          <Button type="submit" size="icon" disabled={!input.trim() || streaming} aria-label="Send message" data-testid="readiness-chat-send-button" className="h-11 w-11 shrink-0 rounded-none bg-[#173f42] text-white hover:bg-[#0d3033]">
            <ArrowUp size={18} />
          </Button>
        </div>
        {error && <p className="mt-3 text-xs font-semibold text-[#9a3e2e]" role="alert" data-testid="readiness-chat-error">{error}</p>}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-[10px] leading-5 text-[#777a72]">
          <p data-testid="readiness-chat-disclaimer">AI reflection only—not therapy, diagnosis, or emergency support. Avoid sensitive details.</p>
          <button type="button" onClick={startNewConversation} className="flex items-center gap-2 font-semibold text-[#455950] hover:text-[#a06f3e]" data-testid="readiness-chat-new-conversation">
            <RefreshCw size={12} /> New conversation
          </button>
        </div>
      </form>
    </div>
  );
};